import { all, call, fork, put, takeLatest } from "redux-saga/effects";

import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import {
  approveRemediationRequests,
  cancelRemediationRequests,
  createRemediationRequests,
  downloadComplianceReport as downloadRemediationReportApi,
  getRemediationCheckDetail,
  getRemediationIamStack,
  getRemediationNotificationSettings,
  getRemediationArtifact,
  getRemediationRequest,
  listRemediationArtifacts,
  listRemediableChecks,
  listRemediationActiveRequests,
  listRemediationInventory,
  listRemediationRequests,
  provideRemediationInputs,
  runNowRemediationRequests,
  scheduleRemediationRequests,
  updateRemediationNotificationSettings,
} from "../../helpers/auth_aws_helper";

import {
  approveRequestsFailure,
  approveRequestsSuccess,
  cancelRequestsFailure,
  cancelRequestsSuccess,
  createRequestsFailure,
  createRequestsSuccess,
  fetchCheckPlaybookFailure,
  fetchCheckPlaybookSuccess,
  fetchActiveRequestsFailure,
  fetchActiveRequestsSuccess,
  fetchIamStackFailure,
  fetchIamStackSuccess,
  fetchInventoryFailure,
  fetchInventorySuccess,
  fetchNotificationSettingsFailure,
  fetchNotificationSettingsSuccess,
  fetchRemediableChecksFailure,
  fetchRemediableChecksSuccess,
  fetchArtifactContentFailure,
  fetchArtifactContentSuccess,
  fetchArtifactsManifestFailure,
  fetchArtifactsManifestSuccess,
  fetchRequestDetailFailure,
  fetchRequestDetailSuccess,
  fetchRequestsFailure,
  fetchRequestsSuccess,
  provideInputsFailure,
  provideInputsSuccess,
  downloadRemediationReportFailure,
  downloadRemediationReportSuccess,
  runNowRequestsFailure,
  runNowRequestsSuccess,
  scheduleRequestsFailure,
  scheduleRequestsSuccess,
  updateNotificationSettingsFailure,
  updateNotificationSettingsSuccess,
} from "./remediationSlice";
import { formatValidationDetailList, pickCustomerMessage } from "../../pages/RemediationHub/components/common/ErrorCodeMap";

/** Normalise API failures (including 422 validation arrays) for slices + snackbars. */
const failPayload = (response, fallback) => {
  const body = response?.data ?? response;
  const validationDetails = Array.isArray(body?.detail)
    ? formatValidationDetailList(body.detail)
    : null;
  const customerMessage = pickCustomerMessage(body);
  const parsed = getApiErrorMessage(response);
  const message =
    customerMessage ||
    (parsed && parsed !== "Request failed."
      ? parsed
      : validationDetails?.length
        ? validationDetails.join(" ")
        : fallback);
  return {
    message,
    customer_message: body?.customer_message,
    error: body?.error || (validationDetails?.length ? "validation" : "internal-error"),
    detail: body?.detail,
    context: body?.context,
    validationDetails: validationDetails?.length ? validationDetails : undefined,
  };
};

const postBody = (response) => response?.data ?? response;

const httpStatusCode = (response) =>
  typeof response?.status === "number" ? response.status : null;

/** Drop HTTP status merged onto GET responses by aws_api_helper.get(). */
const stripHttpStatus = (response) => {
  if (!response || typeof response !== "object" || typeof response.status !== "number") {
    return response;
  }
  const { status, ...rest } = response;
  return rest;
};

/** POST create often returns 201; treat any 2xx as success. */
const isOkResponse = (response) => {
  const code = httpStatusCode(response);
  return code != null && code >= 200 && code < 300;
};

function* fetchRequestsWorker({ payload }) {
  const { silent, ...apiParams } = payload || {};
  try {
    const response = yield call(listRemediationRequests, apiParams);
    if (isOkResponse(response)) {
      yield put(fetchRequestsSuccess({ ...stripHttpStatus(response), params: apiParams }));
    } else {
      yield put(
        fetchRequestsFailure(failPayload(response, "An error occurred while loading remediation requests"))
      );
    }
  } catch (error) {
    yield put(
      fetchRequestsFailure(failPayload(error, "An error occurred while loading remediation requests"))
    );
  }
}

function* fetchRequestDetailWorker({ payload }) {
  try {
    const response = yield call(getRemediationRequest, payload);
    if (response?.request_id != null || response?.id != null) {
      yield put(fetchRequestDetailSuccess(response));
    } else {
      yield put(
        fetchRequestDetailFailure(failPayload(response, "An error occurred while loading the request"))
      );
    }
  } catch (error) {
    yield put(
      fetchRequestDetailFailure(failPayload(error, "An error occurred while loading the request"))
    );
  }
}

function* fetchArtifactsManifestWorker({ payload: requestId }) {
  try {
    const response = yield call(listRemediationArtifacts, requestId);
    if (isOkResponse(response)) {
      yield put(fetchArtifactsManifestSuccess(stripHttpStatus(response)));
    } else {
      yield put(
        fetchArtifactsManifestFailure({
          requestId,
          error: failPayload(response, "Couldn't load artifact list."),
        })
      );
    }
  } catch (error) {
    yield put(
      fetchArtifactsManifestFailure({
        requestId,
        error: failPayload(error, "Couldn't load artifact list."),
      })
    );
  }
}

function* fetchArtifactContentWorker({ payload }) {
  const { requestId, phase } = payload || {};
  try {
    const response = yield call(getRemediationArtifact, requestId, phase);
    if (isOkResponse(response)) {
      yield put(fetchArtifactContentSuccess({ requestId, phase, data: stripHttpStatus(response) }));
    } else {
      yield put(
        fetchArtifactContentFailure({
          requestId,
          phase,
          error: failPayload(response, "Couldn't load artifact content."),
        })
      );
    }
  } catch (error) {
    yield put(
      fetchArtifactContentFailure({
        requestId,
        phase,
        error: failPayload(error, "Couldn't load artifact content."),
      })
    );
  }
}

function* fetchRemediableChecksWorker() {
  try {
    const response = yield call(listRemediableChecks);
    if (isOkResponse(response)) {
      yield put(fetchRemediableChecksSuccess(stripHttpStatus(response)));
    } else {
      yield put(
        fetchRemediableChecksFailure(failPayload(response, "An error occurred while loading remediable checks"))
      );
    }
  } catch (error) {
    yield put(
      fetchRemediableChecksFailure(failPayload(error, "An error occurred while loading remediable checks"))
    );
  }
}

function* fetchCheckPlaybookWorker({ payload }) {
  try {
    const response = yield call(getRemediationCheckDetail, payload);
    if (isOkResponse(response)) {
      yield put(fetchCheckPlaybookSuccess(stripHttpStatus(response)));
    } else {
      yield put(
        fetchCheckPlaybookFailure(failPayload(response, "An error occurred while loading the check playbook"))
      );
    }
  } catch (error) {
    yield put(
      fetchCheckPlaybookFailure(failPayload(error, "An error occurred while loading the check playbook"))
    );
  }
}

function* fetchActiveRequestsWorker({ payload }) {
  const { account_number, check_id } = payload || {};
  if (!account_number || !check_id) return;

  const fail = (error) =>
    fetchActiveRequestsFailure({
      account_number,
      check_id,
      error: failPayload(error, "Couldn't load active remediation requests."),
    });

  try {
    const merged = [];
    let afterId = null;

    for (;;) {
      const params = { limit: 200 };
      if (afterId != null) params.after_id = afterId;

      const response = yield call(
        listRemediationActiveRequests,
        account_number,
        check_id,
        params
      );

      if (!isOkResponse(response)) {
        yield put(fail(response));
        return;
      }

      const { active_requests = [], next_cursor } = stripHttpStatus(response);
      if (Array.isArray(active_requests)) merged.push(...active_requests);

      if (next_cursor == null) break;
      afterId = next_cursor;
    }

    yield put(
      fetchActiveRequestsSuccess({
        account_number,
        check_id,
        active_requests: merged,
      })
    );
  } catch (error) {
    yield put(fail(error));
  }
}

function* fetchInventoryWorker({ payload }) {
  const { invType, account_number, next_token, page_size } = payload || {};
  try {
    const response = yield call(listRemediationInventory, invType, {
      account_number,
      next_token,
      page_size,
    });
    if (isOkResponse(response)) {
      yield put(fetchInventorySuccess({ invType, data: stripHttpStatus(response), append: Boolean(next_token) }));
    } else {
      yield put(
        fetchInventoryFailure({
          invType,
          error: failPayload(response, "An error occurred while loading inventory"),
        })
      );
    }
  } catch (error) {
    yield put(
      fetchInventoryFailure({
        invType,
        error: failPayload(error, "An error occurred while loading inventory"),
      })
    );
  }
}

function* fetchNotificationSettingsWorker() {
  try {
    const response = yield call(getRemediationNotificationSettings);
    if (isOkResponse(response)) {
      yield put(fetchNotificationSettingsSuccess(stripHttpStatus(response)));
    } else {
      yield put(
        fetchNotificationSettingsFailure(failPayload(response, "An error occurred while loading notification settings"))
      );
    }
  } catch (error) {
    yield put(
      fetchNotificationSettingsFailure(failPayload(error, "An error occurred while loading notification settings"))
    );
  }
}

function* updateNotificationSettingsWorker({ payload }) {
  try {
    const response = yield call(updateRemediationNotificationSettings, payload);
    if (isOkResponse(response)) {
      yield put(updateNotificationSettingsSuccess(stripHttpStatus(response)));
    } else {
      yield put(
        updateNotificationSettingsFailure(failPayload(response, "An error occurred while saving notification settings"))
      );
    }
  } catch (error) {
    yield put(
      updateNotificationSettingsFailure(failPayload(error, "An error occurred while saving notification settings"))
    );
  }
}

function* fetchIamStackWorker({ payload }) {
  try {
    const response = yield call(getRemediationIamStack, payload);
    const template = response?.template;
    if (typeof template === "string" && template.length > 0) {
      yield put(
        fetchIamStackSuccess({
          template,
          role_name: response.role_name,
          filename: response.filename,
        })
      );
    } else if (isOkResponse(response)) {
      yield put(
        fetchIamStackFailure(failPayload(response, "The IAM stack template was empty."))
      );
    } else {
      yield put(
        fetchIamStackFailure(failPayload(response, "An error occurred while loading the IAM stack template"))
      );
    }
  } catch (error) {
    yield put(
      fetchIamStackFailure(failPayload(error, "An error occurred while loading the IAM stack template"))
    );
  }
}

function* createRequestsWorker({ payload }) {
  try {
    const response = yield call(createRemediationRequests, payload);
    if (isOkResponse(response)) {
      yield put(createRequestsSuccess(postBody(response)));
    } else {
      yield put(
        createRequestsFailure(failPayload(response, "An error occurred while creating the request"))
      );
    }
  } catch (error) {
    yield put(
      createRequestsFailure(failPayload(error, "An error occurred while creating the request"))
    );
  }
}

function* approveRequestsWorker({ payload }) {
  try {
    const response = yield call(approveRemediationRequests, payload);
    if (isOkResponse(response)) {
      yield put(approveRequestsSuccess(postBody(response)));
    } else {
      yield put(
        approveRequestsFailure(failPayload(response, "An error occurred while approving the request"))
      );
    }
  } catch (error) {
    yield put(
      approveRequestsFailure(failPayload(error, "An error occurred while approving the request"))
    );
  }
}

function* cancelRequestsWorker({ payload }) {
  try {
    const response = yield call(cancelRemediationRequests, payload);
    if (isOkResponse(response)) {
      yield put(cancelRequestsSuccess(postBody(response)));
    } else {
      yield put(
        cancelRequestsFailure(failPayload(response, "An error occurred while cancelling the request"))
      );
    }
  } catch (error) {
    yield put(
      cancelRequestsFailure(failPayload(error, "An error occurred while cancelling the request"))
    );
  }
}

function* runNowRequestsWorker({ payload }) {
  try {
    const response = yield call(runNowRemediationRequests, payload);
    if (isOkResponse(response)) {
      yield put(runNowRequestsSuccess(postBody(response)));
    } else {
      yield put(
        runNowRequestsFailure(failPayload(response, "An error occurred while scheduling run-now"))
      );
    }
  } catch (error) {
    yield put(
      runNowRequestsFailure(failPayload(error, "An error occurred while scheduling run-now"))
    );
  }
}

function* scheduleRequestsWorker({ payload }) {
  try {
    const response = yield call(scheduleRemediationRequests, payload);
    if (isOkResponse(response)) {
      yield put(scheduleRequestsSuccess(postBody(response)));
    } else {
      yield put(
        scheduleRequestsFailure(failPayload(response, "An error occurred while re-scheduling"))
      );
    }
  } catch (error) {
    yield put(
      scheduleRequestsFailure(failPayload(error, "An error occurred while re-scheduling"))
    );
  }
}

function* provideInputsWorker({ payload }) {
  try {
    const response = yield call(provideRemediationInputs, payload);
    if (isOkResponse(response)) {
      yield put(provideInputsSuccess(postBody(response)));
    } else {
      yield put(
        provideInputsFailure(failPayload(response, "An error occurred while saving inputs"))
      );
    }
  } catch (error) {
    yield put(
      provideInputsFailure(failPayload(error, "An error occurred while saving inputs"))
    );
  }
}

function* downloadRemediationReportWorker({ payload }) {
  try {
    const response = yield call(downloadRemediationReportApi, payload);

    if (isOkResponse(response) && response.downloadUrl) {
      yield put(
        downloadRemediationReportSuccess({
          downloadUrl: response.downloadUrl,
          fileName: response.fileName,
        })
      );
    } else {
      yield put(
        downloadRemediationReportFailure(
          failPayload(response, "An error occurred while downloading the remediation report")
        )
      );
    }
  } catch (error) {
    yield put(
      downloadRemediationReportFailure(
        failPayload(error, "An error occurred while downloading the remediation report")
      )
    );
  }
}

function* watchRemediation() {
  yield takeLatest("remediation/fetchRequests", fetchRequestsWorker);
  yield takeLatest("remediation/fetchRequestDetail", fetchRequestDetailWorker);
  yield takeLatest("remediation/fetchArtifactsManifest", fetchArtifactsManifestWorker);
  yield takeLatest("remediation/fetchArtifactContent", fetchArtifactContentWorker);
  yield takeLatest("remediation/fetchRemediableChecks", fetchRemediableChecksWorker);
  yield takeLatest("remediation/fetchCheckPlaybook", fetchCheckPlaybookWorker);
  yield takeLatest("remediation/fetchActiveRequests", fetchActiveRequestsWorker);
  yield takeLatest("remediation/fetchInventory", fetchInventoryWorker);
  yield takeLatest("remediation/fetchNotificationSettings", fetchNotificationSettingsWorker);
  yield takeLatest("remediation/updateNotificationSettings", updateNotificationSettingsWorker);
  yield takeLatest("remediation/fetchIamStack", fetchIamStackWorker);
  yield takeLatest("remediation/createRequests", createRequestsWorker);
  yield takeLatest("remediation/approveRequests", approveRequestsWorker);
  yield takeLatest("remediation/cancelRequests", cancelRequestsWorker);
  yield takeLatest("remediation/runNowRequests", runNowRequestsWorker);
  yield takeLatest("remediation/scheduleRequests", scheduleRequestsWorker);
  yield takeLatest("remediation/provideInputs", provideInputsWorker);
  yield takeLatest("remediation/downloadRemediationReport", downloadRemediationReportWorker);
}

export default function* remediationSaga() {
  yield all([fork(watchRemediation)]);
}
